import { type ConfiguredSchemaReadiness } from '@omnea-digital/extops-schema'
import { useQuery } from '@tanstack/react-query'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { ExtOpsApiClient } from '@omnea-app/packages/universal/extopsApiClient/extopsApiClient'

// One accounting integration per organisation, so the key is organisation-scoped.
export const customAccountingIntegrationQueryKey = ['customAccountingIntegration']

export type CustomAccountingIntegration = {
  customIntegrationId: string
  commitSupplierProcedureTemplateId: string | null
  commitPurchaseOrderProcedureTemplateId: string | null
  configuredSchemaReadiness: ConfiguredSchemaReadiness
}

/**
 * The custom (ExtOps) integration set as the org's accounting integration and the ERP related procedures bound to
 *  it, or null when the org has no custom accounting integration.
 */
export const useGetCustomAccountingIntegration = () => {
  const { getToken } = useAuthenticatedUser()

  return useQuery({
    queryKey: customAccountingIntegrationQueryKey,
    queryFn: async (): Promise<CustomAccountingIntegration | null> => {
      const apiClient = new ExtOpsApiClient({ authorization: await getToken() })
      const { data, error } = await apiClient.GET('/account/accounting-integration')
      if (error?.type === 'NotFoundError') return null
      throwIfApiError(error)
      return {
        customIntegrationId: data.customIntegrationId,
        configuredSchemaReadiness: data.configuredSchemaReadiness,
        commitSupplierProcedureTemplateId: data.commitSupplier?.procedureTemplateId ?? null,
        commitPurchaseOrderProcedureTemplateId: data.commitPurchaseOrder?.procedureTemplateId ?? null,
      }
    },
  })
}
